import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import './CsaDirectory.css';

/**
 * Trade Watcher → CSAs (Support + Admin). Every CSA with their email and the
 * clients they cover. A CSA gets a copy of every naked-position alert email
 * sent for their clients. Support only sees their own company.
 *
 * headers: function returning the request headers (Trade Watcher's, so a
 * Platform Admin previewing Support gets Support's view).
 */
/** A client's name, plus their email when another trader has the same name. */
function clientLabel(c) {
  return c.sameName && c.email ? `${c.name} (${c.email})` : c.name;
}

const PICKER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'This CSA' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'other', label: 'Other CSAs' },
];

/**
 * Pick the clients a CSA covers: search by name or email, filter by who covers
 * them now, tick to assign. A client has one CSA, so ticking one that another
 * CSA covers moves them here.
 */
function ClientPicker({ csa, clients, busy, onAssign }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const isMine = (c) => (c.csa || '').toLowerCase() === csa.key;
  const counts = {
    all: clients.length,
    mine: clients.filter(isMine).length,
    unassigned: clients.filter((c) => !c.csa).length,
    other: clients.filter((c) => c.csa && !isMine(c)).length,
  };
  const needle = q.trim().toLowerCase();
  const shown = clients
    .filter((c) => !needle || `${c.name} ${c.email || ''}`.toLowerCase().includes(needle))
    .filter((c) => (filter === 'mine' ? isMine(c)
      : filter === 'unassigned' ? !c.csa
        : filter === 'other' ? Boolean(c.csa) && !isMine(c)
          : true))
    .sort((a, b) => Number(isMine(b)) - Number(isMine(a)) || Number(!b.csa) - Number(!a.csa) || a.name.localeCompare(b.name));

  return (
    <div className="csa-pick">
      <div className="csa-pick-head">
        <span className="vd-control-label">Clients {csa.name} covers</span>
        <span className="tw-muted csa-pick-count">{counts.mine} of {counts.all} selected</span>
      </div>
      <div className="csa-pick-tools">
        <input
          type="search"
          className="tw-input csa-pick-search"
          placeholder="Search name or email"
          aria-label="Search clients"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="vd-toggle-group" role="group" aria-label="Show clients">
          {PICKER_FILTERS.map((f) => (
            <button key={f.id} type="button" className={`vd-toggle ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
              {f.label} <span className="csa-pick-n">{counts[f.id]}</span>
            </button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="tw-muted csa-pick-empty">{clients.length ? 'No clients match.' : 'No clients are reporting yet.'}</p>
      ) : (
        <ul className="csa-pick-list" aria-label={`Clients ${csa.name} covers`}>
          {shown.map((c) => {
            const mine = isMine(c);
            return (
              <li key={c.key}>
                <label className={`csa-pick-row${mine ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={mine}
                    disabled={Boolean(busy)}
                    onChange={() => onAssign(c.key, mine ? '' : csa.name)}
                  />
                  <span className="csa-pick-who">
                    <span className="csa-pick-name">{c.name}</span>
                    {c.email ? <span className="tw-muted csa-pick-email">{c.email}</span> : null}
                  </span>
                  <span className="tw-muted csa-pick-accts">{c.accounts} acct{c.accounts === 1 ? '' : 's'}</span>
                  <span className={`csa-pick-status${mine ? ' is-mine' : c.csa ? ' is-other' : ''}`}>
                    {busy === `assign:${c.key}` ? 'Saving…' : mine ? csa.name : c.csa ? `Now: ${c.csa}` : 'Unassigned'}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <p className="tw-muted csa-pick-note">A client has one CSA. Ticking a client that another CSA covers moves them here.</p>
    </div>
  );
}

export default function CsaDirectory({ headers, onChanged }) {
  const [csas, setCsas] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [emailDrafts, setEmailDrafts] = useState({});
  const [newCsa, setNewCsa] = useState({ name: '', email: '' });
  const [openKey, setOpenKey] = useState('');

  const call = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...headers() },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed');
    return data;
  }, [headers]);

  const load = useCallback(async () => {
    try {
      const data = await call(API_ENDPOINTS.tradeWatcherCsas);
      setCsas(data.csas || []);
      setClients(data.clients || []);
      setEmailDrafts(Object.fromEntries((data.csas || []).map((c) => [c.key, c.email || ''])));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, done) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await fn();
      await load();
      if (onChanged) onChanged();
      if (done) setNotice(done);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const saveEmail = (csa) => run(`email:${csa.key}`, () => call(API_ENDPOINTS.tradeWatcherCsas, {
    method: 'PUT',
    body: JSON.stringify({ name: csa.name, email: emailDrafts[csa.key] || '' }),
  }), `Saved ${csa.name}'s email.`);

  const assign = (clientKey, csaName) => run(`assign:${clientKey}`, () => call(API_ENDPOINTS.tradeWatcherCsa, {
    method: 'PUT',
    body: JSON.stringify({ clientId: clientKey, csa: csaName }),
  }));

  const removeCsa = (csa) => {
    const n = csa.clients.length;
    if (!window.confirm(`Delete ${csa.name}?${n ? ` Their ${n} client${n === 1 ? '' : 's'} will be unassigned.` : ''}`)) return;
    run(`delete:${csa.key}`, () => call(API_ENDPOINTS.tradeWatcherCsas, {
      method: 'DELETE',
      body: JSON.stringify({ name: csa.name }),
    }), `Deleted ${csa.name}.`);
  };

  const addCsa = (e) => {
    e.preventDefault();
    const name = newCsa.name.trim();
    if (!name) return;
    run('add', async () => {
      await call(API_ENDPOINTS.tradeWatcherCsas, { method: 'PUT', body: JSON.stringify(newCsa) });
      setNewCsa({ name: '', email: '' });
    }, `Added ${name}.`);
  };

  const unassigned = useMemo(() => clients.filter((c) => !c.csa), [clients]);

  return (
    <section className="vd-panel vd-ml-widget csa-dir">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>CSAs</h3>
          <p className="tw-muted tw-section-sub">
            Who covers which clients. Whenever one of a CSA&apos;s clients gets a naked-position alert email,
            the CSA gets a copy at the email below.
          </p>
        </div>
        {csas ? <span className="tw-count">{csas.length}</span> : null}
      </div>

      {error ? <p className="tw-error" role="alert">{error}</p> : null}
      {notice ? <p className="tw-muted tw-status" role="status">{notice}</p> : null}
      {csas === null && !error ? <p className="tw-empty">Loading CSAs…</p> : null}
      {csas && csas.length === 0 ? <p className="tw-empty">No CSAs yet. Add one below.</p> : null}

      {csas && csas.length > 0 ? (
        <ul className="csa-dir-list">
          {csas.map((csa) => {
            const draft = emailDrafts[csa.key] ?? '';
            const dirty = draft.trim().toLowerCase() !== (csa.email || '');
            return (
              <li key={csa.key} className="csa-dir-row">
                <div className="csa-dir-head">
                  <span className="csa-dir-name">{csa.name}</span>
                  <span className="tw-muted csa-dir-meta">
                    {csa.clients.length} client{csa.clients.length === 1 ? '' : 's'}
                    {csa.email ? '' : ' · no email, gets no copies'}
                  </span>
                  <button type="button" className="tw-link csa-dir-delete" onClick={() => removeCsa(csa)} disabled={Boolean(busy)}>
                    Delete
                  </button>
                </div>

                <form
                  className="csa-dir-email"
                  onSubmit={(e) => { e.preventDefault(); if (dirty) saveEmail(csa); }}
                >
                  <input
                    className="tw-input"
                    type="email"
                    placeholder="csa@company.com"
                    aria-label={`${csa.name} email`}
                    value={draft}
                    onChange={(e) => setEmailDrafts((d) => ({ ...d, [csa.key]: e.target.value }))}
                  />
                  <button type="submit" className="vd-btn" disabled={!dirty || Boolean(busy)}>
                    {busy === `email:${csa.key}` ? 'Saving…' : 'Save email'}
                  </button>
                </form>

                <div className="csa-dir-clients">
                  {csa.clients.length ? (
                    <span className="tw-muted csa-dir-summary">
                      {csa.clients.map(clientLabel).join(', ')}
                    </span>
                  ) : (
                    <span className="tw-muted csa-dir-summary">No clients yet.</span>
                  )}
                  <button
                    type="button"
                    className="tw-text-btn"
                    aria-expanded={openKey === csa.key}
                    onClick={() => setOpenKey((k) => (k === csa.key ? '' : csa.key))}
                    disabled={clients.length === 0}
                  >
                    {openKey === csa.key ? 'Done' : 'Edit clients'}
                  </button>
                </div>

                {openKey === csa.key ? (
                  <ClientPicker csa={csa} clients={clients} busy={busy} onAssign={assign} />                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {unassigned.length ? (
        <p className="tw-muted csa-dir-unassigned">
          Not assigned yet: {unassigned.map(clientLabel).join(', ')}
        </p>
      ) : null}

      <form className="csa-dir-add" onSubmit={addCsa}>
        <span className="vd-control-label">Add a CSA</span>
        <input
          className="tw-input"
          placeholder="Name, e.g. Miguel"
          aria-label="New CSA name"
          maxLength={60}
          value={newCsa.name}
          onChange={(e) => setNewCsa((n) => ({ ...n, name: e.target.value }))}
        />
        <input
          className="tw-input"
          type="email"
          placeholder="Email"
          aria-label="New CSA email"
          value={newCsa.email}
          onChange={(e) => setNewCsa((n) => ({ ...n, email: e.target.value }))}
        />
        <button type="submit" className="vd-btn" disabled={!newCsa.name.trim() || Boolean(busy)}>
          {busy === 'add' ? 'Adding…' : 'Add CSA'}
        </button>
      </form>
    </section>
  );
}
